/**
 * Parse an `agape://` recommendation deep-link and navigate the app to the
 * matching screen via the root navigation ref.
 *
 * Contract is defined in awesome-chat-api/docs/rag-implementation.md §4.
 */
import { navigationRef } from '../navigation/rootNavigation';
import type { RecommendationCard } from '../types/rag';
import { CIRCLES_ENABLED } from '../config/flags';

/**
 * Parse an agape:// URL into {host, segments}. Returns null if the URL is
 * not a valid agape-scheme URL we can route.
 */
function parseAgapeUrl(url: string): { host: string; segments: string[] } | null {
    if (!url || typeof url !== 'string') return null;
    if (!url.startsWith('agape://')) return null;
    const rest = url.slice('agape://'.length);
    if (!rest) return null;
    const parts = rest.split('/').filter(Boolean);
    if (parts.length === 0) return null;
    const [host, ...segments] = parts;
    return { host, segments };
}

/**
 * Navigable app "section" cards (agape://<id>) → stack screen name. These come
 * from the backend's static section registry (platform_sections.py) and, unlike
 * entity deep-links, carry no id segment — the whole card is a catalog/tab. Keys
 * are the section ids (deep-link hosts); values are route names in RootStack.
 */
const SECTION_ROUTES: Record<string, string> = {
    home: 'Home',
    schedule: 'Schedule',
    practices: 'Practices',
    'practice-rooms': 'PracticeRooms',
    masters: 'Masters',
    community: 'Community',
    chats: 'Chats',
    fund: 'Fund',
    dashboard: 'Dashboard',
};

/**
 * Route an agape:// URL to the correct stack screen. Safe no-op if the URL
 * can't be parsed or the navigator isn't ready yet (rare).
 *
 * Returns true if navigation was dispatched, false otherwise.
 */
export function navigateFromAgapeUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    const parsed = parseAgapeUrl(url);
    if (!parsed) return false;
    if (!navigationRef.isReady()) return false;
    const { host, segments } = parsed;

    // Typed navigate via the union signature is awkward for dynamic deep-links;
    // cast once and use it as a plain (name, params) function below.
    const navigate = navigationRef.navigate as unknown as (name: string, params?: Record<string, unknown>) => void;

    // Section cards first: a bare top-level destination (agape://practices,
    // agape://masters…) with no id segment maps straight to a stack screen.
    // Without this, section cards fell through to `default` and did nothing.
    const sectionRoute = segments.length === 0 ? SECTION_ROUTES[host] : undefined;
    if (sectionRoute) {
        navigate(sectionRoute, {});
        return true;
    }

    switch (host) {
        case 'master': {
            const id = Number(segments[0]);
            if (!Number.isFinite(id)) return false;
            navigate('MasterDetail', { masterId: id });
            return true;
        }
        case 'practice': {
            const id = Number(segments[0]);
            if (!Number.isFinite(id)) return false;
            navigate('PracticeDetail', { id, type: 'practice' });
            return true;
        }
        case 'live': {
            const id = Number(segments[0]);
            if (!Number.isFinite(id)) return false;
            navigate('LiveDetail', { id });
            return true;
        }
        case 'room': {
            // CORE-502 — a Practice Room card. The segment is the invite code
            // (`/room/7JCiwi`), not the numeric id: that's the address the
            // announcement screen resolves and the one people share.
            const code = segments[0];
            if (!code) return false;
            navigate('RoomAnnouncement', { roomId: code });
            return true;
        }
        case 'about': {
            // A platform material («Виды подписок») lives as one block of the
            // About page, addressed by its backend key: agape://about/<key>.
            // Without this the card rendered, read like a link, and did
            // nothing — the host fell through to `default`.
            const sectionKey = segments[0];
            navigate('AboutPlatform', sectionKey ? { sectionKey } : {});
            return true;
        }
        case 'legal': {
            // CORE-1350 — миссия и этический кодекс приезжают из индекса
            // Agapella как документы платформы. У них своя страница, а не блок
            // «О платформе»: карточка с адресом вкладки вела бы на страницу, где
            // этого текста нет.
            const docId = segments[0];
            if (!docId) return false;
            navigate('LegalDoc', { docId });
            return true;
        }
        case 'post': {
            navigate('Community', {});
            return true;
        }
        case 'event': {
            // Master/platform event — we currently surface these on the Lives
            // screen; fall back to Community if no dedicated screen exists.
            const id = Number(segments[0]);
            if (Number.isFinite(id)) {
                navigate('Lives', { eventId: id });
            } else {
                navigate('Community', {});
            }
            return true;
        }
        case 'circle': {
            // AMB-228 — circles hidden from MVP; circle deep-links from
            // push notifications (AMB-57) and recommendation cards
            // intentionally fall through to the default false branch so
            // the caller can apply its own fallback (e.g. land on home).
            if (!CIRCLES_ENABLED) return false;
            // Expected shape: agape://circle/{circleId}/event/{eventId}
            const circleId = Number(segments[0]);
            if (!Number.isFinite(circleId)) return false;
            navigate('CircleChat', { circleId });
            return true;
        }
        default:
            return false;
    }
}

/**
 * Fallback router when a card has no deep_link — derive a destination from
 * entity_type + entity_id. Keeps the card clickable even if the backend
 * forgot to populate deep_link (shouldn't happen, but belt & suspenders).
 */
export function navigateFromCard(card: RecommendationCard): boolean {
    if (card.deep_link && navigateFromAgapeUrl(card.deep_link)) return true;
    // Section cards carry a non-numeric entity_id (the section id) — resolve them
    // before the numeric-id guard below, which would otherwise reject them.
    if (card.entity_type === 'section') {
        return navigateFromAgapeUrl(`agape://${card.entity_id}`);
    }
    // A platform material's id is `about:<lang>:<key>` — the key is the address,
    // the language is an indexing detail. Same reason as above: not a number.
    if (card.entity_type === 'platform_material') {
        // Материал платформы адресуется префиксом: `about:<lang>:<key>` — блок
        // страницы «О платформе», `legal:<lang>:<key>` — отдельный документ
        // (CORE-1350). Язык здесь деталь индексации.
        const [prefix, , ...rest] = card.entity_id.split(':');
        const key = rest.length ? rest.join(':') : card.entity_id.split(':').pop();
        if (!key) return false;
        return navigateFromAgapeUrl(`agape://${prefix === 'legal' ? 'legal' : 'about'}/${key}`);
    }
    const id = Number(card.entity_id);
    if (!Number.isFinite(id)) return false;
    switch (card.entity_type) {
        case 'master_profile':
            return navigateFromAgapeUrl(`agape://master/${id}`);
        case 'video_practice':
            return navigateFromAgapeUrl(`agape://practice/${id}`);
        case 'live':
            return navigateFromAgapeUrl(`agape://live/${id}`);
        case 'master_post':
            return navigateFromAgapeUrl(`agape://post/${id}`);
        case 'master_event':
        case 'platform_event':
            return navigateFromAgapeUrl(`agape://event/${id}`);
        case 'circle_event':
            // Without parent circleId we can't deep-link precisely.
            return navigateFromAgapeUrl(`agape://community`);
        case 'watch_party':
            // Only reachable if deep_link went missing — the room's own address
            // is its invite code, which entity_id (the numeric key) is not. The
            // announcement screen accepts both forms, so this still lands.
            return navigateFromAgapeUrl(`agape://room/${id}`);
        default:
            return false;
    }
}
